import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import './Navbar.css';
import MsaLogo from './MsaLogo';

const navLinks = [
  { label: 'Home', to: '/' },
  { label: 'Planos', to: '/planos' },
  { label: 'Contato', to: '/contatos' },
  { label: 'FAQ', to: '/', isFaq: true },
];

const CENTRAL_ASSINANTE_URL = 'https://ixc.faynetrj.com.br/central_assinante_web/login';

function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const navRef = useRef(null);

  const closeMenu = () => setMenuOpen(false);

  // Rola até a seção FAQ da Home; se estiver em outra página, navega antes de rolar
  const goToFaq = (event) => {
    event.preventDefault();
    closeMenu();

    const scrollToFaqSection = () => {
      document.getElementById('faq')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    if (location.pathname === '/') {
      scrollToFaqSection();
    } else {
      navigate('/', { state: { scrollTo: 'faq' } });
    }
  };

  // Sombra de elevação ao rolar a página
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Fecha o menu ao trocar de rota
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Fecha com Esc, clique fora e trava o scroll do body enquanto o menu mobile está aberto
  useEffect(() => {
    if (!menuOpen) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    const onClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onClickOutside);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onClickOutside);
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  const actionLinks = (
    <>
      <Link
        to="/2via"
        className="navbar__segunda-via"
        onClick={closeMenu}
        title="2ª Via da Fatura"
      >
        <span className="navbar__segunda-via-icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 2h16v20l-3-2-2 2-2-2-2 2-2-2-2 2-3-2z" />
            <line x1="8" y1="7" x2="16" y2="7" />
            <line x1="8" y1="11" x2="16" y2="11" />
            <line x1="8" y1="15" x2="13" y2="15" />
          </svg>
        </span>
        <span className="navbar__segunda-via-label">2ª Via da Fatura</span>
      </Link>

      <a
        href={CENTRAL_ASSINANTE_URL}
        className="navbar__avatar"
        target="_blank"
        rel="noopener noreferrer"
        title="Central do Assinante"
        onClick={closeMenu}
      >
        <span className="navbar__avatar-icon">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
        </span>
        <span className="navbar__avatar-label">Central do Assinante</span>
      </a>
    </>
  );

  return (
    <header className={`navbar ${scrolled ? 'navbar--scrolled' : ''}`} ref={navRef}>
      <div className="navbar__container">
        {/* Logo */}
        <Link to="/" className="navbar__logo">
          <MsaLogo />
        </Link>

        {/* Nav links */}
        <nav
          id="navbar-menu"
          className={`navbar__nav ${menuOpen ? 'navbar__nav--open' : ''}`}
        >
          <ul className="navbar__list">
            {navLinks.map((link) => (
              <li key={link.label} className="navbar__item">
                <Link
                  to={link.to}
                  className={`navbar__link ${!link.isFaq && location.pathname === link.to ? 'navbar__link--active' : ''}`}
                  onClick={link.isFaq ? goToFaq : closeMenu}
                >
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>

          {/* Ações (visíveis apenas no menu mobile) */}
          <div className="navbar__nav-actions">
            {actionLinks}
          </div>
        </nav>

        <div className="navbar__actions">
          {/* Ações (visíveis apenas no header, telas grandes) */}
          <div className="navbar__actions-desktop">
            {actionLinks}
          </div>

          {/* Hamburger (mobile) */}
          <button
            className={`navbar__hamburger ${menuOpen ? 'navbar__hamburger--open' : ''}`}
            onClick={() => setMenuOpen((open) => !open)}
            aria-label={menuOpen ? 'Fechar menu' : 'Abrir menu'}
            aria-expanded={menuOpen}
            aria-controls="navbar-menu"
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </div>

      {/* Linha laranja inferior */}
      <div className="navbar__accent-line" />
    </header>
  );
}

export default Navbar;
